/**
 * Service-Level Cache Utilities
 * Provides in-memory caching with TTL for API responses
 * Reduces redundant database calls across components
 */

interface CacheEntry<T> {
  data: T
  timestamp: number
  companyId: string | null
}

// Default cache TTL: 2 minutes (short enough to stay fresh, long enough to help)
const DEFAULT_CACHE_TTL = 2 * 60 * 1000

// Cache storage
const cache = new Map<string, CacheEntry<any>>()

// Cache invalidation callbacks
const invalidationCallbacks = new Map<string, Set<() => void>>()

/**
 * Get cached data if valid
 * @param key Cache key
 * @param companyId Current company ID for isolation
 * @param ttl Time-to-live in milliseconds
 */
export function getFromCache<T>(
  key: string,
  companyId: string | null,
  ttl: number = DEFAULT_CACHE_TTL
): T | null {
  const entry = cache.get(key)

  if (!entry) return null

  // Check if cache belongs to same company
  if (entry.companyId !== companyId) return null

  // Check if cache is still valid
  if (Date.now() - entry.timestamp > ttl) {
    cache.delete(key)
    return null
  }

  return entry.data as T
}

/**
 * Store data in cache
 * @param key Cache key
 * @param data Data to cache
 * @param companyId Current company ID for isolation
 */
export function setInCache<T>(
  key: string,
  data: T,
  companyId: string | null
): void {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    companyId
  })
}

/**
 * Invalidate specific cache key
 */
export function invalidateCache(key: string): void {
  cache.delete(key)

  // Notify subscribers
  const callbacks = invalidationCallbacks.get(key)
  if (callbacks) {
    callbacks.forEach(cb => cb())
  }
}

/**
 * Invalidate all cache entries matching a prefix
 * e.g., invalidateCacheByPrefix('invoices') clears invoices_sale, invoices_purchase, etc.
 */
export function invalidateCacheByPrefix(prefix: string): void {
  const keysToDelete: string[] = []

  cache.forEach((_, key) => {
    if (key.startsWith(prefix)) {
      keysToDelete.push(key)
    }
  })

  keysToDelete.forEach(key => {
    cache.delete(key)
    const callbacks = invalidationCallbacks.get(key)
    if (callbacks) {
      callbacks.forEach(cb => cb())
    }
  })
}

/**
 * Clear all cache entries
 */
export function clearAllCache(): void {
  cache.clear()
}

/**
 * Subscribe to cache invalidation for a specific key
 * Returns unsubscribe function
 */
export function onCacheInvalidate(key: string, callback: () => void): () => void {
  if (!invalidationCallbacks.has(key)) {
    invalidationCallbacks.set(key, new Set())
  }

  invalidationCallbacks.get(key)!.add(callback)

  return () => {
    const callbacks = invalidationCallbacks.get(key)
    if (callbacks) {
      callbacks.delete(callback)
    }
  }
}

/**
 * Cache keys constants for consistency
 */
export const CACHE_KEYS = {
  INVOICES_SALE: 'invoices_sale',
  INVOICES_PURCHASE: 'invoices_purchase',
  INVOICES_QUOTE: 'invoices_quote',
  INVOICES_ALL: 'invoices_all',
  PARTIES_ALL: 'parties_all',
  PARTIES_CUSTOMER: 'parties_customer',
  PARTIES_SUPPLIER: 'parties_supplier',
  PARTIES_WITH_OUTSTANDING: 'parties_outstanding',
  ITEMS_ALL: 'items_all',
  EXPENSES_ALL: 'expenses_all',
  SETTINGS: 'settings'
} as const
